import axios from 'axios';

/**
 * Content-type to send for a file. Must be byte-identical between the
 * `/case/upload-url` request (which signs the URL for this exact content
 * type) and the GCS PUT itself, or the signed URL will reject the upload.
 */
export const getUploadContentType = (file) => file.type || 'application/pdf';

/**
 * PUT a file directly to a GCS signed URL, reporting progress as it goes.
 *
 * Deliberately uses bare axios rather than the `api` instance from
 * `src/api/api.js` — that instance's interceptors attach a Firebase auth
 * header and rewrite the base URL, neither of which GCS signed URLs accept
 * (a signed URL is pre-authorized via its query string; extra headers or a
 * different host will cause GCS to reject the request).
 *
 * @param {string} signedUrl - GCS V4 signed URL to PUT to.
 * @param {File} file - File to upload.
 * @param {string} contentType - Must match what was sent to /case/upload-url.
 * @param {{onProgress?: (p: {loaded: number, total: number, percent: number}) => void, signal?: AbortSignal}} [options]
 */
export async function uploadFileWithProgress(signedUrl, file, contentType, { onProgress, signal } = {}) {
    await axios.put(signedUrl, file, {
        headers: { 'Content-Type': contentType },
        signal,
        onUploadProgress: (event) => {
            if (!onProgress) return;
            const loaded = event.loaded ?? 0;
            const total = event.total ?? file.size ?? 0;
            const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
            onProgress({ loaded, total, percent });
        },
    });
}
